/**
 * Hook for handling Finix fraud detection
 */
import { useCallback } from 'react';
export function useFinixRadarAction({ paymentsResource, startPolling, setError, setIsLoading, hookOptionsRef, }) {
    const handleFinixRadar = useCallback(async (payment, actionData, options = {}, handlePaymentAction) => {
        const radarConfig = actionData.metadata?.radar;
        if (!radarConfig) {
            console.error('Finix radar config missing from payment action');
            return;
        }
        try {
            // Dynamically load Finix SDK if not already loaded
            if (typeof window !== 'undefined' && typeof window.Finix?.Auth !== 'function') {
                const existingScript = document.querySelector('script[src="https://js.finix.com/v/1/finix.js"]');
                if (!existingScript) {
                    const script = document.createElement('script');
                    script.src = 'https://js.finix.com/v/1/finix.js';
                    script.async = true;
                    document.head.appendChild(script);
                    await new Promise((resolve, reject) => {
                        script.onload = () => {
                            console.log('Finix SDK loaded successfully');
                            resolve();
                        };
                        script.onerror = () => reject(new Error('Failed to load Finix SDK'));
                    });
                }
                else {
                    // Wait for existing script to load
                    await new Promise((resolve, reject) => {
                        const checkFinix = () => {
                            if (typeof window.Finix?.Auth === 'function') {
                                resolve();
                            }
                            else {
                                setTimeout(checkFinix, 100);
                            }
                        };
                        checkFinix();
                        setTimeout(() => reject(new Error('Timeout waiting for Finix SDK')), 10000);
                    });
                }
            }
            // Get session key from Finix using callback to ensure initialization is complete
            const sessionKey = await new Promise((resolve, reject) => {
                const timeoutId = setTimeout(() => {
                    reject(new Error('Timeout waiting for Finix Auth initialization'));
                }, 10000);
                // Initialize Finix Auth with callback
                const FinixAuth = window.Finix.Auth(radarConfig.environment, radarConfig.merchantId, () => {
                    // Callback fired when Finix Auth is ready
                    clearTimeout(timeoutId);
                    const key = FinixAuth.getSessionKey();
                    console.log('Finix Auth initialized, session key:', key);
                    if (key) {
                        resolve(key);
                    }
                    else {
                        reject(new Error('No session key returned from Finix after initialization'));
                    }
                });
                // Also try getting session key immediately in case it's already ready
                const immediateKey = FinixAuth.getSessionKey();
                if (immediateKey) {
                    clearTimeout(timeoutId);
                    console.log('Finix session key obtained immediately:', immediateKey);
                    resolve(immediateKey);
                }
            });
            console.log('Finix fraud session key obtained:', sessionKey);
            // Save radar session to database
            await paymentsResource.saveRadarSession({
                orderId: radarConfig.orderId,
                finixRadarSessionId: sessionKey,
                finixRadarSessionData: {
                    sessionKey,
                    merchantId: radarConfig.merchantId,
                    environment: radarConfig.environment,
                    createdAt: new Date().toISOString(),
                },
            });
            console.log('Finix radar session saved to database');
            // Resume payment by calling completePaymentAfterAction
            const resumedPayment = await paymentsResource.completePaymentAfterAction(payment.id);
            console.log('Payment resumed after Finix radar:', resumedPayment);
            // Handle the resumed payment response
            if (resumedPayment.status === 'declined' || resumedPayment.status === 'failed') {
                // Payment declined or failed - extract error message from response
                const errorMsg = resumedPayment.error?.message
                    || resumedPayment.error?.processorMessage
                    || 'Payment declined';
                console.error('❌ [usePayment] Payment declined after Finix radar:', errorMsg);
                setError(errorMsg);
                setIsLoading(false);
                options.onFailure?.(errorMsg);
                options.onPaymentFailed?.({
                    code: resumedPayment.error?.code || 'PAYMENT_DECLINED',
                    message: errorMsg,
                    payment: resumedPayment,
                });
                // Hook-level callback (universal handler)
                if (hookOptionsRef.current?.onPaymentFailed) {
                    hookOptionsRef.current.onPaymentFailed(errorMsg, {
                        isRedirectReturn: false,
                    });
                }
            }
            else if (resumedPayment.status === 'succeeded') {
                // Payment succeeded
                setIsLoading(false);
                const response = {
                    paymentId: resumedPayment.id,
                    payment: resumedPayment,
                    order: resumedPayment.order,
                };
                // Hook-level callback (universal handler)
                if (hookOptionsRef.current?.onPaymentCompleted) {
                    await hookOptionsRef.current.onPaymentCompleted(resumedPayment, {
                        isRedirectReturn: false,
                        order: response.order,
                    });
                }
                options.onSuccess?.(response);
                options.onPaymentSuccess?.(response);
            }
            else if (resumedPayment.requireAction !== 'none' && resumedPayment.requireActionData) {
                // Payment requires another action (e.g., 3DS)
                await handlePaymentAction(resumedPayment, options);
            }
            else {
                // Start polling for final status
                startPolling(resumedPayment.id, {
                    onRequireAction: (updatedPayment) => {
                        void handlePaymentAction(updatedPayment, options);
                    },
                    onSuccess: async (successPayment) => {
                        setIsLoading(false);
                        const response = {
                            paymentId: successPayment.id,
                            payment: successPayment,
                            order: successPayment.order,
                        };
                        // Hook-level callback (universal handler)
                        if (hookOptionsRef.current?.onPaymentCompleted) {
                            await hookOptionsRef.current.onPaymentCompleted(successPayment, {
                                isRedirectReturn: false,
                                order: response.order,
                            });
                        }
                        options.onSuccess?.(response);
                        options.onPaymentSuccess?.(response);
                    },
                    onFailure: async (errorMsg) => {
                        setError(errorMsg);
                        setIsLoading(false);
                        // Hook-level callback (universal handler)
                        if (hookOptionsRef.current?.onPaymentFailed) {
                            await hookOptionsRef.current.onPaymentFailed(errorMsg, {
                                isRedirectReturn: false,
                            });
                        }
                        options.onFailure?.(errorMsg);
                        options.onPaymentFailed?.({
                            code: 'PAYMENT_FAILED',
                            message: errorMsg,
                            payment,
                        });
                    },
                });
            }
        }
        catch (radarError) {
            const errorMsg = radarError instanceof Error ? radarError.message : 'Finix fraud detection failed';
            console.error('Finix radar error:', radarError);
            setError(errorMsg);
            setIsLoading(false);
            options.onFailure?.(errorMsg);
            options.onPaymentFailed?.({
                code: 'FINIX_RADAR_FAILED',
                message: errorMsg,
                payment,
            });
        }
    }, [paymentsResource, startPolling, setError, setIsLoading, hookOptionsRef]);
    return { handleFinixRadar };
}
